import { Helmet } from 'react-helmet-async';

interface WebsiteSchemaProps {
  name: string;
  url: string;
  description?: string;
  /**
   * Enable SearchAction for sitelinks search box in Google
   * Requires a working search endpoint at /search?q={search_term_string}
   */
  enableSearch?: boolean;
  searchUrlTemplate?: string;
  potentialAction?: {
    type: 'SearchAction' | 'OrderAction';
    target?: string;
    query?: string;
  }[];
}

/**
 * WebSite structured data component following schema.org specification.
 * Generates JSON-LD markup for website-level information including sitelinks searchbox.
 *
 * @see https://schema.org/WebSite
 * @see https://developers.google.com/search/docs/appearance/structured-data/sitelinks-searchbox
 */
export const WebsiteSchema = ({
  name,
  url,
  description,
  enableSearch = false,
  searchUrlTemplate,
  potentialAction,
}: WebsiteSchemaProps) => {
  if (!name || !url) {
    return null;
  }

  const websiteSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
    ...(description && { description }),
  };

  // Add search action for sitelinks searchbox
  if (enableSearch) {
    const searchTarget = searchUrlTemplate || `${url}/search?q={search_term_string}`;
    websiteSchema.potentialAction = [
      {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: searchTarget,
        },
        'query-input': 'required name=search_term_string',
      },
    ];
  }

  // Add custom potential actions (e.g., OrderAction for booking)
  if (potentialAction && potentialAction.length > 0) {
    const existingActions = (websiteSchema.potentialAction as unknown[]) || [];
    websiteSchema.potentialAction = [
      ...existingActions,
      ...potentialAction.map((action) => ({
        '@type': action.type,
        ...(action.target && { target: action.target }),
        ...(action.query && { 'query-input': action.query }),
      })),
    ];
  }

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
    </Helmet>
  );
};

export default WebsiteSchema;
